/**
 * How a prefill (the server's, or the local fallback) meets a draft the engineer may already have changed. Pure functions.
 *   baseline  the normalised keys of every line ever offered, per list. A line never offered before may be appended; a line
 *             offered before and since deleted by the engineer is never put back.
 *   touched   the engineer changed the draft. Until then a new prefill simply replaces it (the editor model does that); after,
 *             it is only merged here: lines are appended, nothing is removed, and no edit is overwritten.
 */
import { MAX_LIST_ITEMS } from "./editorRules";
import { normKey } from "./prefillLines";

export const LISTS = Object.freeze(["symptoms", "steps"]);
const META_OF = Object.freeze({ symptoms: "lineMeta", steps: "stepMeta" });
const sessionKeys = (session, list) => (session[list] || []).map(normKey);

export const emptyBaseline = () => ({ symptoms: [], steps: [] });

/** The baseline after `session` was offered: what it already held plus its own lines. */
export function offeredBaseline(baseline, session) {
  const next = {};
  LISTS.forEach((list) => {
    next[list] = [...new Set([...((baseline && baseline[list]) || []), ...sessionKeys(session, list)])];
  });
  return next;
}

const blank = (text) => !String(text || "").trim();

function appendNew(draft, baseline, session, list) {
  const offered = new Set(baseline[list] || []);
  const rows = draft[list].filter((t) => !blank(t));
  const have = new Set(rows.map(normKey));
  const added = [];
  const meta = { ...(draft[META_OF[list]] || {}) };
  (session[list] || []).forEach((text) => {
    const key = normKey(text);
    if (!key || offered.has(key) || have.has(key) || rows.length + added.length >= MAX_LIST_ITEMS) return;
    have.add(key);
    added.push(text);
    const lineMeta = (session[META_OF[list]] || {})[text];
    if (lineMeta) meta[text] = lineMeta;
  });
  if (!added.length) return { items: draft[list], meta: draft[META_OF[list]] };
  return { items: [...rows, ...added], meta };
}

/**
 * Merges `session` into a draft the engineer has changed. Returns the new {draft, baseline}. Only lines never offered before are
 * appended (up to the list cap); nothing is removed or rewritten, and the root cause and the fix are left as they are.
 */
export function mergeTouched({ draft, baseline }, session) {
  const base = baseline || emptyBaseline();
  const next = { ...draft };
  let changed = false;
  LISTS.forEach((list) => {
    const merged = appendNew(draft, base, session, list);
    if (merged.items !== draft[list]) {
      next[list] = merged.items;
      next[META_OF[list]] = merged.meta;
      changed = true;
    }
  });
  const nextBase = offeredBaseline(base, session);
  return { draft: changed ? next : draft, baseline: nextBase };
}
